/**
 * Investor-Portal Tabellen-View (#1138 Tag 3) — GuV/Bilanz/Cashflow + Chart + Export.
 */
import { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import PeriodToggle, { buildPeriodQuery, isCustomPeriod } from './components/PeriodToggle';
import ExportButton from './components/ExportButton';

const API = import.meta.env.VITE_API_URL || '';

function fmtEur(v) {
  if (v === null || v === undefined) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined) return '–';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} %`;
}
function deltaColor(v) {
  if (v === null || v === undefined) return 'text-[#6B7280]';
  if (v > 0) return 'text-emerald-600';
  if (v < 0) return 'text-red-600';
  return 'text-[#6B7280]';
}
function labelize(position) {
  return position
    .replace(/^guv_|^bilanz_|^cashflow_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function InvestorTabelle({ bereich, title }) {
  const currentYear = new Date().getFullYear();
  const [periodState, setPeriodState] = useState({
    period: 'ytd',
    year: currentYear,
    month: new Date().getMonth() + 1,
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    fromDate: null,
    toDate: null,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAggregatesOnly, setShowAggregatesOnly] = useState(false);

  const periodQuery = useMemo(() => buildPeriodQuery(periodState), [periodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/investor/${bereich}?${periodQuery}`, { credentials: 'include' });
        if (!abort && res.ok) {
          const data = await res.json();
          setRows(data.rows || []);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [bereich, periodQuery]);

  const filteredRows = useMemo(() => {
    if (!showAggregatesOnly) return rows;
    return rows.filter(r => r.is_aggregate);
  }, [rows, showAggregatesOnly]);

  const chartData = useMemo(() => {
    const tops = [...rows]
      .filter(r => r.plan !== 0 || r.ist !== 0)
      .sort((a, b) => Math.abs(b.plan || 0) - Math.abs(a.plan || 0))
      .slice(0, 10);
    return tops.map(r => ({
      position: labelize(r.position).slice(0, 20),
      Plan: Math.abs(r.plan || 0),
      IST: Math.abs(r.ist || 0),
    }));
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-semibold text-[#1A1A2E]">{title}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodToggle
            period={periodState.period}
            year={periodState.year}
            month={periodState.month}
            quarter={periodState.quarter}
            fromDate={periodState.fromDate}
            toDate={periodState.toDate}
            onChange={setPeriodState}
          />
          <ExportButton bereich={bereich} periodQuery={periodQuery} />
        </div>
      </div>

      {isCustomPeriod(periodState) && (
        <div className="text-xs text-[#1A1A2E] bg-[#FFF8E1] border border-[#FFE082] rounded-lg px-3 py-2 flex items-start gap-2">
          <span className="text-[#F59E0B] mt-px">●</span>
          <div>
            <strong>Tagesgenau-Modus:</strong> IST-Werte werden live aus Odoo geladen (
            {periodState.fromDate} – {periodState.toDate}). Plan-Werte sind anteilig pro Tag hochgerechnet.
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <h2 className="text-sm font-semibold text-[#1A1A2E] mb-3">Top-10 Positionen (Plan vs IST)</h2>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="position" stroke="#6B7280" fontSize={10} angle={-30} textAnchor="end" height={60} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v) => fmtEur(v)}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Plan" fill="#9CA3AF" />
                <Bar dataKey="IST" fill="#338edc" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <label className="text-xs text-[#4B5563] flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showAggregatesOnly} onChange={e => setShowAggregatesOnly(e.target.checked)} />
          Nur Aggregate (Σ-Zeilen)
        </label>
        <div className="text-xs text-[#9CA3AF]">{filteredRows.length} von {rows.length} Positionen</div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Position</th>
              <th className="text-right px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Plan</th>
              <th className="text-right px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">IST</th>
              <th className="text-right px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Δ Abs</th>
              <th className="text-right px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Δ %</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Daten</td></tr>
            ) : filteredRows.map(r => (
              <tr key={r.position} className={`border-t border-[#F1F2F4] hover:bg-[#F8F9FC] ${r.is_aggregate ? 'bg-[#F8F9FC]/40 font-medium' : ''}`}>
                <td className="px-4 py-2 text-[#1A1A2E]">
                  {labelize(r.position)}
                  {r.is_aggregate && <span className="ml-1.5 text-[10px] text-[#338edc]" title="Aggregat">Σ</span>}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-[#4B5563]">{fmtEur(r.plan)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-[#1A1A2E] font-medium">{fmtEur(r.ist)}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${deltaColor(r.delta_abs)}`}>{fmtEur(r.delta_abs)}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${deltaColor(r.delta_pct)}`}>{fmtPct(r.delta_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
